import { useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Pencil, DollarSign, Loader2, AlertCircle, Plus } from 'lucide-react';
import { cn } from '@/lib/utils';

interface RateRangeErrors {
  min?: string;
  max?: string;
}

interface RateRangeCardProps {
  rateRangeMin: string;
  rateRangeMax: string;
  isEditing: boolean;
  isSaving: boolean;
  errors: RateRangeErrors;
  onEditClick: () => void;
  onCancel: () => void;
  onSave: () => void;
  onMinChange: (value: string) => void;
  onMaxChange: (value: string) => void;
  onValidate: (min: string, max: string) => RateRangeErrors;
}

export function RateRangeCard({
  rateRangeMin,
  rateRangeMax,
  isEditing,
  isSaving,
  errors,
  onEditClick,
  onCancel,
  onSave,
  onMinChange,
  onMaxChange,
  onValidate,
}: RateRangeCardProps) {
  const isEmpty = !rateRangeMin && !rateRangeMax && !isEditing;
  const previousMinRef = useRef<string>('');
  const previousMaxRef = useRef<string>('');
  const prevIsEditingRef = useRef<boolean>(false);

  // Track previous values when editing starts
  useEffect(() => {
    // Only capture the previous values when transitioning from non-editing to editing
    if (isEditing && !prevIsEditingRef.current) {
      previousMinRef.current = rateRangeMin;
      previousMaxRef.current = rateRangeMax;
    }
    prevIsEditingRef.current = isEditing;
  }, [isEditing, rateRangeMin, rateRangeMax]);

  // Handle cancel - restore previous values
  const handleCancel = () => {
    onMinChange(previousMinRef.current);
    onMaxChange(previousMaxRef.current);
    onCancel();
  };

  return (
    <div className={cn(
      "rounded-xl sm:rounded-[20px] p-4 sm:p-6 shadow-sm",
      isEmpty
        ? "bg-amber-50/50 border-2 border-dashed border-amber-500/30"
        : "bg-white border border-slate-200"
    )}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <DollarSign className="h-5 w-5 text-slate-700" />
          <h3 className="text-lg font-semibold text-slate-900">Rate Range</h3>
          {isEmpty && (
            <Badge className="bg-amber-500 text-white rounded-[48px] px-3 sm:px-4 py-1 text-xs">
              Incomplete
            </Badge>
          )}
        </div>
        {!isEditing && !isEmpty && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onEditClick}
            className="text-[#4669ed]"
          >
            <Pencil className="h-4 w-4" />
          </Button>
        )}
      </div>

      {isEditing ? (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="rateMin" className="text-sm">Min ($/hr)</Label>
              <Input
                id="rateMin"
                type="number"
                placeholder="50"
                value={rateRangeMin}
                onChange={(e) => {
                  onMinChange(e.target.value);
                  onValidate(e.target.value, rateRangeMax);
                }}
                onBlur={() => {
                  onValidate(rateRangeMin, rateRangeMax);
                }}
                min="1"
                step="1"
                required
                className={errors.min ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}
              />
              {errors.min && (
                <p className="text-[10px] text-red-600 flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  {errors.min}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="rateMax" className="text-sm">Max ($/hr)</Label>
              <Input
                id="rateMax"
                type="number"
                placeholder="200"
                value={rateRangeMax}
                onChange={(e) => {
                  onMaxChange(e.target.value);
                  onValidate(rateRangeMin, e.target.value);
                }}
                onBlur={() => {
                  onValidate(rateRangeMin, rateRangeMax);
                }}
                min="1"
                step="1"
                required
                className={errors.max ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}
              />
              {errors.max && (
                <p className="text-[10px] text-red-600 flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  {errors.max}
                </p>
              )}
            </div>
          </div>
          <div className="flex gap-2 pt-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleCancel} 
              disabled={isSaving}
              className="flex-1 rounded-full"
            >
              Cancel
            </Button>
            <Button 
              size="sm" 
              onClick={onSave} 
              disabled={isSaving || Object.keys(errors).length > 0}
              className="flex-1 bg-[#4669ed] hover:bg-[#2b42a3] rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save'
              )}
            </Button>
          </div>
        </div>
      ) : (
        <div>
          {rateRangeMin || rateRangeMax ? (
            <p className="text-slate-900 font-medium">
              {rateRangeMin && rateRangeMax ? 
                (rateRangeMin === rateRangeMax ? `Up to $${rateRangeMax}/hr` : `$${rateRangeMin}-${rateRangeMax}/hr`) :
              rateRangeMin ? `$${rateRangeMin}+/hr` :
              `Up to $${rateRangeMax}/hr`}
            </p>
          ) : (
            <div className="flex flex-col items-center py-4">
              {/* Icon Circle */}
              <div className="w-20 h-20 rounded-[40px] bg-slate-200 flex items-center justify-center mb-6">
                <DollarSign className="w-6 h-6 text-slate-500" />
              </div>

              {/* Title */}
              <h3 className="text-xl font-normal text-slate-900 text-center mb-2">
                Set Your Rate Range
              </h3>

              {/* Description */}
              <p className="text-sm text-slate-500 text-center max-w-[280px] leading-relaxed mb-6">
                Share your hourly rate range to help clients understand your pricing.
              </p>

              {/* CTA Button */}
              <Button
                onClick={onEditClick}
                className="bg-[#4669ed] hover:bg-[#2b42a3] rounded-[48px] px-6"
              >
                <Plus className="w-5 h-5" />
                Set Rate Range
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
